/** Silent device fingerprint → Path A / B / C. No UI chooser. */
import { EDGE_MODELS } from './edgeHardware.mjs';

export function isIpad(ua, { touchMac = false } = {}) {
  const u = String(ua || '').toLowerCase();
  return /ipad/.test(u) || (/macintosh/.test(u) && touchMac);
}

export function parseOs(ua, { touchMac = false } = {}) {
  const u = String(ua || '').toLowerCase();
  if (/ipad/.test(u) || (/macintosh/.test(u) && touchMac)) return 'ios';
  if (/iphone|ipod/.test(u)) return 'ios';
  if (/android/.test(u)) return 'android';
  if (/mac os x|macintosh/.test(u)) return 'macos';
  if (/windows/.test(u)) return 'windows';
  if (/cros/.test(u)) return 'chromeos';
  if (/linux/.test(u)) return 'linux';
  return 'unknown';
}

export function isDesktopOrAndroid(os) {
  return os === 'android' || os === 'macos' || os === 'windows' || os === 'linux' || os === 'chromeos';
}

export function routeFromFingerprint({
  os = 'unknown',
  ipad = false,
  hasWebGpu = false,
  deviceMemoryGb = 0,
  hardwareConcurrency = 0,
  maxBufferSize = 0,
  batteryLevel = null,
  charging = null,
} = {}) {
  const dying = batteryLevel != null && batteryLevel < 0.15 && charging !== true;
  const bufGb = Number(maxBufferSize || 0) / 1024 ** 3;
  // deviceMemory=0 means the browser hid it (Windows Chrome). That is unknown, not "tiny".
  const knownSmallRam = deviceMemoryGb >= 1 && deviceMemoryGb < 4;
  const knownSmallBuf = maxBufferSize > 0 && bufGb > 0 && bufGb < 0.5;
  const tightMemory = knownSmallRam || knownSmallBuf;
  const ios = os === 'ios';

  if (!hasWebGpu || dying) {
    return {
      path: 'C',
      role: 'leech',
      swarmRole: 'fallback',
      model: null,
      tier: 'none',
      acceptJobs: false,
      wakeLock: false,
      reason: !hasWebGpu ? 'no_webgpu' : 'battery_low',
    };
  }

  if (ios) {
    const docked = ipad || charging === true;
    if (docked) {
      const use3b = bufGb >= 2 || hardwareConcurrency >= 8;
      return {
        path: 'A',
        role: 'swarm-worker',
        swarmRole: 'active-worker',
        model: use3b ? EDGE_MODELS['3b'] : EDGE_MODELS['1b'],
        tier: use3b ? '3b' : '1b',
        acceptJobs: true,
        wakeLock: true,
        reason: ipad ? 'ipad_docked' : 'ios_charging',
      };
    }
    const use1b = bufGb >= 0.5 || deviceMemoryGb >= 4 || hardwareConcurrency >= 6;
    return {
      path: 'B',
      role: 'chat-only',
      swarmRole: 'chat-only',
      model: use1b ? EDGE_MODELS['1b'] : EDGE_MODELS['0.5b'],
      tier: use1b ? '1b' : '0.5b',
      acceptJobs: true,
      wakeLock: false,
      reason: 'ios_chat_only',
    };
  }

  if (isDesktopOrAndroid(os) && !dying) {
    const use8b = !tightMemory && (bufGb >= 4 || deviceMemoryGb >= 16);
    const use3b = !tightMemory;
    const model = use8b ? EDGE_MODELS['8b'] : (use3b ? EDGE_MODELS['3b'] : EDGE_MODELS['1b']);
    const tier = use8b ? '8b' : (use3b ? '3b' : '1b');
    return {
      path: 'A',
      role: 'swarm-worker',
      swarmRole: 'active-worker',
      model,
      tier,
      acceptJobs: true,
      wakeLock: true,
      reason: tightMemory ? 'workhorse_small_ram' : (use8b ? 'workhorse_8b' : 'workhorse'),
    };
  }

  return {
    path: 'B',
    role: 'chat-only',
    swarmRole: 'chat-only',
    model: EDGE_MODELS['0.5b'],
    tier: '0.5b',
    acceptJobs: false,
    wakeLock: false,
    reason: 'default_light',
  };
}

export async function collectFingerprint(nav = globalThis.navigator, doc = globalThis.document) {
  if (!nav) {
    return routeFromFingerprint({ hasWebGpu: false });
  }
  const touchMac = Boolean(doc && 'ontouchend' in doc);
  const os = parseOs(nav.userAgent || '', { touchMac });
  const hardwareConcurrency = Number(nav.hardwareConcurrency || 0);
  const deviceMemoryGb = Number(nav.deviceMemory || 0);
  let hasWebGpu = Boolean(nav.gpu);
  let maxBufferSize = 0;
  if (nav.gpu && typeof nav.gpu.requestAdapter === 'function') {
    try {
      const adapter = await nav.gpu.requestAdapter();
      maxBufferSize = Number(adapter?.limits?.maxBufferSize || 0);
      if (!adapter) hasWebGpu = false;
    } catch {
      hasWebGpu = false;
    }
  }
  let batteryLevel = null;
  let charging = null;
  try {
    if (typeof nav.getBattery === 'function') {
      const bat = await nav.getBattery();
      batteryLevel = typeof bat.level === 'number' ? bat.level : null;
      charging = Boolean(bat.charging);
    }
  } catch { /* Safari */ }
  const route = routeFromFingerprint({
    os,
    ipad: isIpad(nav.userAgent || '', { touchMac }),
    hasWebGpu,
    deviceMemoryGb,
    hardwareConcurrency,
    maxBufferSize,
    batteryLevel,
    charging,
  });
  return {
    ...route,
    os,
    hardwareConcurrency,
    deviceMemoryGb,
    maxBufferSize,
    batteryLevel,
    charging,
    hasWebGpu,
  };
}
